import React from 'react'
import { Box, CircularProgress, Divider, Typography } from '@mui/material'
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined'
import { ApiPushGetRecipient } from '../../apiService/Core/Apicall'
import DarkDialog from './DarkDialog'
import { InlineAlert } from './PushDisabledNotice'
import StatusPill from './StatusPill'
import { useGuardedFetch } from './useGuardedFetch'
import { fmtDate, getErrorMessage } from './pushUtils'
import type { Delivery, Recipient } from './types'

interface Props {
    campaignId: string
    userId: string | null
    onClose: () => void
}

const attemptLine = (a: Delivery['attempts'][number]) => {
    const outcome = a.outcome === 'ACCEPTED' ? 'ACCEPTED (by Firebase)' : 'FAILED'
    const code = a.outcome === 'FAILED' && a.errorCode ? ` (${a.errorCode})` : ''
    return `Attempt ${a.n} → ${outcome}${code}`
}

const RecipientDialog: React.FC<Props> = ({ campaignId, userId, onClose }) => {
    const fetcher = React.useCallback(
        () => ApiPushGetRecipient(campaignId, userId as string),
        [campaignId, userId]
    )
    const { data, error, loading } = useGuardedFetch<any>(fetcher, { enabled: Boolean(userId), silentLoads: true })
    const recipient: Recipient | undefined = data?.data?.recipient
    const deliveries: Delivery[] = data?.data?.deliveries ?? []

    return (
        <DarkDialog
            open={Boolean(userId)}
            onClose={onClose}
            title="Recipient details"
            icon={<PersonSearchOutlinedIcon sx={{ fontSize: 18 }} />}
            cancelText="Close"
        >
            {loading && !recipient ? (
                <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress sx={{ color: '#ff6a3d' }} /></Box>
            ) : error ? (
                <InlineAlert severity="error">{getErrorMessage(error)}</InlineAlert>
            ) : recipient ? (
                <>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e0e0e0' }}>{recipient.name || '-'}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#6a6a6a' }}>{recipient.email || '-'}</Typography>
                    <Box sx={{ my: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <StatusPill status={recipient.status} />
                        {recipient.retryState && recipient.retryState !== 'NONE' && <StatusPill status={recipient.retryState} />}
                    </Box>
                    {(recipient.failureReason || recipient.skipReason) && (
                        <Typography sx={{ fontSize: 13, color: '#e0e0e0', mb: 1 }}>
                            Reason: {recipient.failureReason || recipient.skipReason}
                        </Typography>
                    )}
                    <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>
                        Sources: {recipient.sources?.length ? recipient.sources.join(', ') : '-'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#7a7a7a' }}>Last attempt: {fmtDate(recipient.lastAttemptAt)}</Typography>
                    <Divider sx={{ my: 2, borderColor: '#333' }} />
                    <Typography sx={{ fontSize: 12, color: '#898989', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>
                        Devices ({deliveries.length})
                    </Typography>
                    {deliveries.length === 0 ? (
                        <Typography sx={{ fontSize: 13, color: '#6a6a6a' }}>No eligible device was registered for this user.</Typography>
                    ) : (
                        deliveries.map((d, i) => (
                            <Box key={`${d.deviceId}-${i}`} sx={{ mb: 2, p: 1.5, border: '1px solid #333', borderRadius: '10px', background: '#1a1a1a' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 13, color: '#e0e0e0' }}>
                                        {String(d.platform ?? '').toUpperCase()} device ...{String(d.deviceId).slice(-6)}
                                    </Typography>
                                    <StatusPill status={d.status} />
                                </Box>
                                {(d.attempts ?? []).length === 0 ? (
                                    <Typography sx={{ fontSize: 11, color: '#6a6a6a', mt: 0.5 }}>No attempts yet.</Typography>
                                ) : (
                                    d.attempts.map((a) => (
                                        <Typography key={a.n} sx={{ fontSize: 13, color: a.outcome === 'ACCEPTED' ? '#10b981' : '#ef4444', mt: 0.5 }}>
                                            {attemptLine(a)}{a.permanent ? ' - permanent' : ''}{' '}
                                            <Typography component="span" sx={{ fontSize: 11, color: '#6a6a6a' }}>{fmtDate(a.at)}</Typography>
                                            {a.outcome === 'FAILED' && (a.providerCode || a.providerMessage) ? (
                                                <Typography sx={{ fontSize: 11, color: '#9a9a9a', mt: 0.2, wordBreak: 'break-word' }}>
                                                    Firebase said: {[a.providerCode, a.providerMessage].filter(Boolean).join(' - ')}
                                                </Typography>
                                            ) : null}
                                        </Typography>
                                    ))
                                )}
                            </Box>
                        ))
                    )}
                </>
            ) : null}
        </DarkDialog>
    )
}

export default RecipientDialog
